import { useState, useEffect, useRef, useMemo, RefObject } from 'react';

type ScreenType = 'lg' | 'md' | 'sm';

const getScreenType = (width: number): ScreenType => {
  if (width >= 1200) return 'lg';
  if (width >= 768) return 'md';
  return 'sm';
};

export const useScreenType = (screen: ScreenType): [ScreenType, boolean] => {
  const [screenType, setScreenType] = useState<ScreenType>(getScreenType(window.innerWidth));

  useEffect(() => {
    const handleResize = () => {
      setScreenType(getScreenType(window.innerWidth));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return [screenType, screen === screenType];
};

export const useHTMLChanges = (element: Node | null, callback: MutationCallback): MutationObserver | null => {
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    const observer = new MutationObserver(callback);
    if (element) {
      observer.observe(element, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      observerRef.current = observer;
    }

    return () => {
      observer.disconnect();
    };
  }, [element, callback]);

  return observerRef.current;
};

const useScreenResize = (screen: ScreenType, ref: RefObject<HTMLElement>, ignore?: boolean): void => {
  const [trigger, setTrigger] = useState<number>(new Date().getTime());
  const tableContent = useMemo<HTMLElement | null>(
    () => ref.current?.querySelector('.ant-table-content') ?? null,
    [ref.current]
  );
  const [, isMd] = useScreenType(screen);

  const onMutation = () => setTrigger(new Date().getTime());

  useEffect(() => {
    if (ignore || !tableContent) return undefined;
    const innerTableContent = tableContent.querySelectorAll<HTMLElement>(
      '.ant-table-expanded-row-level-1 .ant-table-content'
    );
    const innerTableExpanders = tableContent.querySelectorAll<HTMLElement>('.expanded-row');
    const handleScroll = () => {
      if (isMd) {
        innerTableContent.forEach(c => {
          c.style.marginLeft = `${tableContent.scrollLeft}px`;
          c.scrollLeft = tableContent.scrollLeft;
        });
        innerTableExpanders.forEach(c => {
          c.style.marginLeft = `${tableContent.scrollLeft}px`;
          c.scrollLeft = tableContent.scrollLeft;
        });
      } else {
        innerTableContent.forEach(c => {
          c.style.marginLeft = '0';
          c.scrollLeft = 0;
        });
        innerTableExpanders.forEach(c => {
          c.style.marginLeft = '0';
          c.scrollLeft = 0;
        });
      }
    };
    const handleResize = () => {
      if (isMd) {
        const width = `${tableContent.getBoundingClientRect().width}px`;
        innerTableContent.forEach(c => {
          c.style.width = width;
        });
        innerTableExpanders.forEach(c => {
          c.style.width = width;
        });
      } else {
        innerTableContent.forEach(c => {
          c.style.width = '';
        });
        innerTableExpanders.forEach(c => {
          c.style.width = '';
        });
      }
      handleScroll();
    };
    const handleScrollInner = (e: Event) => {
      const target = e.target as HTMLElement;
      if (isMd) {
        tableContent.scrollLeft = target.scrollLeft;
        innerTableContent.forEach(c => {
          c.style.marginLeft = `${target.scrollLeft}px`;
          c.scrollLeft = target.scrollLeft;
        });
        innerTableExpanders.forEach(c => {
          c.style.marginLeft = `${target.scrollLeft}px`;
          c.scrollLeft = target.scrollLeft;
        });
      } else {
        tableContent.scrollLeft = 0;
        innerTableContent.forEach(c => {
          c.style.marginLeft = '0';
          c.scrollLeft = 0;
        });
        innerTableExpanders.forEach(c => {
          c.style.marginLeft = '0';
          c.scrollLeft = 0;
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    tableContent.addEventListener('scroll', handleScroll);
    innerTableContent.forEach(c => {
      c.addEventListener('scroll', handleScrollInner);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      tableContent.removeEventListener('scroll', handleScroll);
      innerTableContent.forEach(c => {
        c.removeEventListener('scroll', handleScrollInner);
      });
    };
  }, [isMd, ref, trigger]);
  useHTMLChanges(ref.current, onMutation);
};

export default useScreenResize;
